import type { FailureReason, LocalizationEvent } from "./runtime";
import type { ServerErrorEvent, ServerErrorFallbackReason } from "./server-errors";

// Aggregate counters over a runtime's observer events, for diagnostics and
// tests. They live as long as the page's runtime: no persistence, no network,
// no console output and no fleet aggregation. Every dimension is a fixed enum
// of this package; keys, languages, revisions and parameters are never kept.

/** Typed as a full record, so a new FailureReason fails the build until it is listed here. */
const FAILURE_REASONS: Readonly<Record<FailureReason, 0>> = {
  network: 0,
  timeout: 0,
  aborted: 0,
  http_status: 0,
  content_type: 0,
  too_large: 0,
  integrity: 0,
  manifest_invalid: 0,
  artifact_invalid: 0,
  contract_incompatible: 0,
  language_unavailable: 0,
  url_rejected: 0,
  unexpected: 0,
};
/** Typed as a full record, so a new ServerErrorFallbackReason fails the build until it is listed here. */
const SERVER_ERROR_FALLBACKS: Readonly<Record<ServerErrorFallbackReason, 0>> = {
  invalid_envelope: 0,
  unknown_id: 0,
  invalid_params: 0,
  invalid_code_params: 0,
};
const FALLBACK_SOURCES = ["cache", "embedded"] as const;
const CACHE_REJECTIONS = ["storage", "corrupt", "foreign"] as const;

type FallbackSource = (typeof FALLBACK_SOURCES)[number];
type CacheRejection = (typeof CACHE_REJECTIONS)[number];

/** One counter increment; the same fixed dimensions as the counters. */
export type LocalizationMetric =
  | { readonly metric: "missing_key" | "format_error" | "message_fallback" | "preference_unavailable" | "cache_write_failed" }
  | { readonly metric: "load_failed"; readonly reason: FailureReason }
  | { readonly metric: "catalog_fallback"; readonly source: FallbackSource }
  | { readonly metric: "cache_rejected"; readonly reason: CacheRejection }
  | { readonly metric: "server_error_fallback"; readonly reason: ServerErrorFallbackReason };

export interface LocalizationCounters {
  /** t() calls of an invalid, foreign or absent key (rendered as the key). */
  readonly missing_key: number;
  /** t() calls whose message could not be formatted with its parameters (rendered as the key). */
  readonly format_error: number;
  /** t() calls served by a later language of the active language's chain (untranslated text). */
  readonly message_fallback: number;
  /** Requests whose own verified manifest does not publish the explicit profile language; never from a cached set or offline. */
  readonly preference_unavailable: number;
  /** Failed catalog requests of the latest request, by reason; superseded requests are not failures. */
  readonly load_failed: Readonly<Record<FailureReason, number>>;
  /** Delivery failures after which the client served its cached set or embedded catalog. */
  readonly catalog_fallback: Readonly<Record<FallbackSource, number>>;
  /** Cached sets removed on read, by reason. */
  readonly cache_rejected: Readonly<Record<CacheRejection, number>>;
  readonly cache_write_failed: number;
  /**
   * Server error presentations not used as sent, by fixed reason (an `l10n` that is
   * malformed, has an id this build does not know or invalid params; an existing code
   * whose detail fields are invalid). The id and params are never kept.
   */
  readonly server_error_fallback: Readonly<Record<ServerErrorFallbackReason, number>>;
}

export interface LocalizationMetrics {
  /** Pass as the runtime's and the server error resolver's `onEvent`. Never throws. */
  readonly onEvent: (event: LocalizationEvent | ServerErrorEvent) => void;
  /** A frozen copy of the current counters. */
  snapshot(): LocalizationCounters;
}

export interface LocalizationMetricsOptions {
  /** Called after each increment; a throwing sink is ignored. */
  readonly sink?: (metric: LocalizationMetric) => void;
}

const zeros = <K extends string>(names: readonly K[]): Record<K, number> => Object.fromEntries(names.map((name) => [name, 0])) as Record<K, number>;

/** Increments `record[name]` only for a name of its fixed set, so no event can add a dimension. */
function bump<K extends string>(record: Record<K, number>, name: string): name is K {
  if (!Object.hasOwn(record, name)) return false;
  record[name as K] += 1;
  return true;
}

export function createLocalizationMetrics(options: LocalizationMetricsOptions = {}): LocalizationMetrics {
  const scalars = zeros(["missing_key", "format_error", "message_fallback", "preference_unavailable", "cache_write_failed"] as const);
  const loadFailed: Record<FailureReason, number> = { ...FAILURE_REASONS };
  const catalogFallback = zeros(FALLBACK_SOURCES);
  const cacheRejected = zeros(CACHE_REJECTIONS);
  const serverErrorFallback: Record<ServerErrorFallbackReason, number> = { ...SERVER_ERROR_FALLBACKS };

  const notify = (metric: LocalizationMetric) => {
    try {
      options.sink?.(metric);
    } catch {
      // A failing sink must not break localization.
    }
  };

  function onEvent(event: LocalizationEvent | ServerErrorEvent): void {
    try {
      switch (event.type) {
        case "missing_key":
        case "format_error":
        case "message_fallback":
        case "preference_unavailable":
        case "cache_write_failed":
          scalars[event.type] += 1;
          notify({ metric: event.type });
          return;
        case "load_failed":
          if (bump(loadFailed, event.reason)) notify({ metric: "load_failed", reason: event.reason });
          return;
        case "catalog_fallback":
          if (bump(catalogFallback, event.source)) notify({ metric: "catalog_fallback", source: event.source });
          return;
        case "cache_rejected":
          if (bump(cacheRejected, event.reason)) notify({ metric: "cache_rejected", reason: event.reason });
          return;
        case "server_error_fallback":
          if (bump(serverErrorFallback, event.reason)) notify({ metric: "server_error_fallback", reason: event.reason });
          return;
        default:
          // activated and any later event type: normal operation, not counted.
          return;
      }
    } catch {
      // An unexpected event shape is ignored.
    }
  }

  return {
    onEvent,
    snapshot: () =>
      Object.freeze({
        ...scalars,
        load_failed: Object.freeze({ ...loadFailed }),
        catalog_fallback: Object.freeze({ ...catalogFallback }),
        cache_rejected: Object.freeze({ ...cacheRejected }),
        server_error_fallback: Object.freeze({ ...serverErrorFallback }),
      }),
  };
}
